import React, { useState, useEffect } from 'react';
import { FileText, Heart, Zap, Target, Brain, Star, Sparkles, Check, Loader2, Image, Upload } from 'lucide-react';
import { Language } from '../types';

type IconType = 'FileText' | 'Heart' | 'Zap' | 'Target' | 'Brain' | 'Star' | 'Sparkles' | 'Image' | 'Upload';

const ICON_MAP: Record<IconType, React.FC<{ size: number; className: string }>> = {
  FileText, Heart, Zap, Target, Brain, Star, Sparkles, Image, Upload
};

interface AnalysisStep {
  id: string;
  label: string;
  icon: IconType;
  completed: boolean;
  current: boolean;
}

interface AnalysisLoadingOverlayProps {
  isVisible: boolean;
  type: 'questionnaire' | 'photos' | 'sync';
  progress: number;
  language: Language;
  photoCount?: number;
  onComplete?: () => void;
}

const TRANSLATIONS: Record<Language, {
  questionnaire: {
    title: string;
    subtitle: string;
    steps: string[];
    completing: string;
  };
  photos: {
    title: string;
    subtitle: string;
    steps: string[];
    completing: string;
  };
  sync: {
    title: string;
    subtitle: string;
    steps: string[];
    completing: string;
  };
}> = {
  Italian: {
    questionnaire: {
      title: 'Sto analizzando le tue risposte',
      subtitle: 'Creo un profilo unico basato sulla tua storia',
      steps: [
        'Leggo le tue risposte...',
        'Identifico i tuoi valori profondi...',
        'Comprendo le tue motivazioni...',
        'Scopro i tuoi obiettivi...',
        'Analizzo i tuoi pattern comportamentali...',
        'Costruisco il tuo profilo personale...',
        'Finalizzazione in corso...'
      ],
      completing: 'Profilo pronto!'
    },
    photos: {
      title: 'Analizzo i tuoi ricordi',
      subtitle: 'Ogni foto racconta qualcosa di te',
      steps: [
        'Carico le tue foto...',
        'Riconosco persone e luoghi...',
        'Identifico emozioni e contesti...',
        'Trovo connessioni con la tua storia...',
        'Integro i ricordi nel tuo profilo...'
      ],
      completing: 'Ricordi salvati!'
    },
    sync: {
      title: 'Sincronizzo i tuoi dati',
      subtitle: 'Preparo la tua esperienza personalizzata',
      steps: [
        'Salvo il tuo profilo...',
        'Carico i tuoi momenti...',
        'Analizzo i pattern...',
        'Preparo la dashboard...'
      ],
      completing: 'Tutto pronto!'
    }
  },
  English: {
    questionnaire: {
      title: 'Analyzing your answers',
      subtitle: 'Creating a unique profile based on your story',
      steps: [
        'Reading your answers...',
        'Identifying your core values...',
        'Understanding your motivations...',
        'Discovering your goals...',
        'Analyzing your behavioral patterns...',
        'Building your personal profile...',
        'Finalizing...'
      ],
      completing: 'Profile ready!'
    },
    photos: {
      title: 'Analyzing your memories',
      subtitle: 'Every photo tells something about you',
      steps: [
        'Uploading your photos...',
        'Recognizing people and places...',
        'Identifying emotions and contexts...',
        'Finding connections with your story...',
        'Integrating memories into your profile...'
      ],
      completing: 'Memories saved!'
    },
    sync: {
      title: 'Syncing your data',
      subtitle: 'Preparing your personalized experience',
      steps: [
        'Saving your profile...',
        'Loading your moments...',
        'Analyzing patterns...',
        'Preparing dashboard...'
      ],
      completing: 'All ready!'
    }
  },
  Spanish: {
    questionnaire: {
      title: 'Analizando tus respuestas',
      subtitle: 'Creando un perfil único basado en tu historia',
      steps: [
        'Leyendo tus respuestas...',
        'Identificando tus valores profundos...',
        'Comprendiendo tus motivaciones...',
        'Descubriendo tus objetivos...',
        'Analizando tus patrones de comportamiento...',
        'Construyendo tu perfil personal...',
        'Finalizando...'
      ],
      completing: '¡Perfil listo!'
    },
    photos: {
      title: 'Analizando tus recuerdos',
      subtitle: 'Cada foto cuenta algo de ti',
      steps: [
        'Cargando tus fotos...',
        'Reconociendo personas y lugares...',
        'Identificando emociones y contextos...',
        'Encontrando conexiones con tu historia...',
        'Integrando recuerdos en tu perfil...'
      ],
      completing: '¡Recuerdos guardados!'
    },
    sync: {
      title: 'Sincronizando tus datos',
      subtitle: 'Preparando tu experiencia personalizada',
      steps: [
        'Guardando tu perfil...',
        'Cargando tus momentos...',
        'Analizando patrones...',
        'Preparando dashboard...'
      ],
      completing: '¡Todo listo!'
    }
  },
  German: {
    questionnaire: {
      title: 'Analysiere deine Antworten',
      subtitle: 'Erstelle ein einzigartiges Profil basierend auf deiner Geschichte',
      steps: [
        'Lese deine Antworten...',
        'Identifiziere deine Kernwerte...',
        'Verstehe deine Motivationen...',
        'Entdecke deine Ziele...',
        'Analysiere deine Verhaltensmuster...',
        'Erstelle dein persönliches Profil...',
        'Finalisiere...'
      ],
      completing: 'Profil bereit!'
    },
    photos: {
      title: 'Analysiere deine Erinnerungen',
      subtitle: 'Jedes Foto erzählt etwas über dich',
      steps: [
        'Lade deine Fotos hoch...',
        'Erkenne Personen und Orte...',
        'Identifiziere Emotionen und Kontexte...',
        'Finde Verbindungen zu deiner Geschichte...',
        'Integriere Erinnerungen in dein Profil...'
      ],
      completing: 'Erinnerungen gespeichert!'
    },
    sync: {
      title: 'Synchronisiere deine Daten',
      subtitle: 'Bereite deine personalisierte Erfahrung vor',
      steps: [
        'Speichere dein Profil...',
        'Lade deine Momente...',
        'Analysiere Muster...',
        'Bereite Dashboard vor...'
      ],
      completing: 'Alles bereit!'
    }
  },
  French: {
    questionnaire: {
      title: 'J\'analyse tes réponses',
      subtitle: 'Je crée un profil unique basé sur ton histoire',
      steps: [
        'Je lis tes réponses...',
        'J\'identifie tes valeurs profondes...',
        'Je comprends tes motivations...',
        'Je découvre tes objectifs...',
        'J\'analyse tes patterns comportementaux...',
        'Je construis ton profil personnel...',
        'Finalisation en cours...'
      ],
      completing: 'Profil prêt!'
    },
    photos: {
      title: 'J\'analyse tes souvenirs',
      subtitle: 'Chaque photo raconte quelque chose de toi',
      steps: [
        'Je charge tes photos...',
        'Je reconnais les personnes et les lieux...',
        'J\'identifie les émotions et les contextes...',
        'Je trouve des connexions avec ton histoire...',
        'J\'intègre les souvenirs dans ton profil...'
      ],
      completing: 'Souvenirs sauvegardés!'
    },
    sync: {
      title: 'Je synchronise tes données',
      subtitle: 'Je prépare ton expérience personnalisée',
      steps: [
        'Je sauvegarde ton profil...',
        'Je charge tes moments...',
        'J\'analyse les patterns...',
        'Je prépare le dashboard...'
      ],
      completing: 'Tout est prêt!'
    }
  }
};

const STEP_ICONS: Record<string, IconType[]> = {
  questionnaire: ['FileText', 'Heart', 'Zap', 'Target', 'Brain', 'Star', 'Sparkles'],
  photos: ['Upload', 'Target', 'Heart', 'Brain', 'Sparkles'],
  sync: ['FileText', 'Image', 'Brain', 'Sparkles']
};

const AnalysisLoadingOverlay: React.FC<AnalysisLoadingOverlayProps> = ({
  isVisible,
  type,
  progress,
  language,
  photoCount = 0,
  onComplete
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  const t = TRANSLATIONS[language] || TRANSLATIONS.English;
  const content = t[type];
  const icons = STEP_ICONS[type];

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Trigger animation after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(true);
        });
      });
    } else {
      // Fade out first, then unmount
      setIsAnimatingIn(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setCurrentStepIndex(0);
        setDisplayProgress(0);
        setIsCompleting(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const totalSteps = content.steps.length;
    const stepProgress = 100 / totalSteps;
    const targetStep = Math.min(Math.floor(progress / stepProgress), totalSteps - 1);

    if (progress >= 95 && !isCompleting) {
      setIsCompleting(true);
      setTimeout(() => {
        onComplete?.();
      }, 800);
    }

    const stepInterval = setInterval(() => {
      setCurrentStepIndex(prev => {
        if (prev < targetStep) return prev + 1;
        return prev;
      });
    }, 1500);

    const progressInterval = setInterval(() => {
      setDisplayProgress(prev => {
        const target = progress;
        if (prev < target) {
          return Math.min(prev + 0.5, target);
        }
        return prev;
      });
    }, 30);

    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
    };
  }, [isVisible, progress, content.steps.length, isCompleting, onComplete]);

  if (!shouldRender) return null;

  const CurrentIcon = ICON_MAP[icons[Math.min(currentStepIndex, icons.length - 1)]];
  const currentMessage = isCompleting 
    ? content.completing 
    : content.steps[Math.min(currentStepIndex, content.steps.length - 1)];

  return (
    <div
      className="fixed inset-0 z-[10000] bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 flex flex-col items-center justify-center px-6 sm:px-8"
      style={{
        opacity: isAnimatingIn ? 1 : 0,
        transition: 'opacity 0.3s ease'
      }}
    >
      {/* Static background blurs - no animations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-indigo-500/20 rounded-full blur-[100px] sm:blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] bg-purple-500/20 rounded-full blur-[80px] sm:blur-[100px]" />
        <div className="absolute top-1/2 right-1/3 w-[150px] sm:w-[200px] h-[150px] sm:h-[200px] bg-blue-500/15 rounded-full blur-[60px] sm:blur-[80px]" />
      </div>

      <div className="relative z-10 flex flex-col items-center max-w-md w-full">
        {/* Icon container - static, only spinner animates */}
        <div className="relative mb-6 sm:mb-8">
          <div
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-2xl"
            style={{
              background: isCompleting
                ? 'linear-gradient(to bottom right, #10b981, #14b8a6)'
                : 'linear-gradient(to bottom right, #6366f1, #9333ea)',
              boxShadow: isCompleting
                ? '0 25px 50px -12px rgba(16, 185, 129, 0.3)'
                : '0 25px 50px -12px rgba(99, 102, 241, 0.3)'
            }}
          >
            {isCompleting ? (
              <Check size={32} className="text-white" />
            ) : (
              <CurrentIcon size={28} className="text-white sm:w-8 sm:h-8" />
            )}
          </div>
        </div>

        <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-2">
          {content.title}
        </h2>
        <p className="text-sm sm:text-base text-white/60 text-center mb-6 sm:mb-8 font-medium">
          {content.subtitle}
          {type === 'photos' && photoCount > 0 && ` (${photoCount} ${language === 'Italian' ? 'foto' : language === 'Spanish' ? 'fotos' : language === 'German' ? 'Fotos' : language === 'French' ? 'photos' : 'photos'})`}
        </p>

        {/* Current step message - static */}
        <div className="h-7 sm:h-8 mb-4 sm:mb-5 flex items-center justify-center">
          <p className={`text-base sm:text-lg font-semibold text-center ${isCompleting ? 'text-emerald-400' : 'text-white'}`}>
            {currentMessage}
          </p>
        </div>

        {type !== 'sync' ? (
          <>
            {/* Progress bar */}
            <div className="w-full max-w-xs mb-5 sm:mb-6">
              <div className="h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${displayProgress}%`,
                    background: isCompleting
                      ? 'linear-gradient(to right, #34d399, #2dd4bf)'
                      : 'linear-gradient(to right, #818cf8, #a78bfa, #f472b6)',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-white/40 font-medium">
                <span>{Math.round(displayProgress)}%</span>
                <span>{isCompleting ? '✓' : `${currentStepIndex + 1}/${content.steps.length}`}</span>
              </div>
            </div>

            {/* Step dots - static colors */}
            <div className="flex gap-1.5 sm:gap-2 mb-6 sm:mb-8 flex-wrap justify-center">
              {content.steps.map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: i < currentStepIndex || isCompleting
                      ? '#34d399'
                      : i === currentStepIndex && !isCompleting
                        ? '#ffffff'
                        : 'rgba(255,255,255,0.2)',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="mb-6 sm:mb-8" />
        )}

        {/* "Don't close app" message with spinner */}
        {!isCompleting && (
          <div className="flex items-center gap-2 text-white/40">
            <div className="spinner spinner-sm spinner-white" />
            <span className="text-xs font-medium">
              {language === 'Italian' ? 'Non chiudere l\'app' :
               language === 'Spanish' ? 'No cierres la app' :
               language === 'German' ? 'App nicht schließen' :
               language === 'French' ? 'Ne ferme pas l\'app' :
               'Don\'t close the app'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisLoadingOverlay;
